import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Sparkles, X, Link2, Star } from "lucide-react";
import GenerateArticleDialog from "./GenerateArticleDialog";

const ArticleComposer = ({
  stack,
  onRemove,
  onClear,
  onGenerated,
}: {
  stack: string[];
  onRemove: (kw: string) => void;
  onClear: () => void;
  onGenerated: (articleId: string) => void;
}) => {
  const [internalUrl, setInternalUrl] = useState("https://maroctransfers.com/");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [primaryKw, setPrimaryKw] = useState("");
  const primary = stack.includes(primaryKw) ? primaryKw : stack[0];

  const openDialog = () => {
    if (stack.length === 0) { toast.error("Ajoutez au moins un mot-clé"); return; }
    if (!/^https?:\/\//i.test(internalUrl)) { toast.error("URL de backlink invalide"); return; }
    setDialogOpen(true);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" /> Rédaction IA (Gemini)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="text-xs text-muted-foreground mb-2">Mots-clés confirmés ({stack.length}) — l'étoile désigne le mot-clé principal</p>
          {stack.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">Ajoutez des mots-clés depuis la recherche ou la bibliothèque.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {stack.map((k) => (
                <Badge key={k} variant={k === primary ? "default" : "secondary"} className={`px-3 py-1.5 text-sm ${k === primary ? "bg-gradient-gold text-primary-foreground" : ""}`}>
                  <button type="button" title="Définir comme mot-clé principal" onClick={() => setPrimaryKw(k)} className="mr-1.5">
                    <Star className={`h-3.5 w-3.5 ${k === primary ? "fill-current" : "opacity-50"}`} />
                  </button>
                  {k}{k === primary && <span className="ml-1.5 text-[10px] uppercase font-semibold">Principal</span>}
                  <button onClick={() => onRemove(k)} className="ml-2 hover:text-destructive"><X className="h-3 w-3" /></button>
                </Badge>
              ))}
            </div>
          )}
        </div>

        <div>
          <label className="text-sm font-medium flex items-center gap-2 mb-1">
            <Link2 className="h-4 w-4 text-primary" /> URL de maillage interne (Backlink cible)
          </label>
          <Input
            value={internalUrl}
            onChange={(e) => setInternalUrl(e.target.value)}
            placeholder="https://maroctransfers.com/page-cible"
          />
          <p className="text-xs text-muted-foreground mt-1">Gemini insérera un lien naturel pointant vers cette URL.</p>
        </div>

        <Button onClick={openDialog} disabled={stack.length === 0} className="bg-gradient-gold text-primary-foreground w-full sm:w-auto">
          <Sparkles className="mr-2 h-4 w-4" />
          Générer un article
        </Button>

        <GenerateArticleDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          keywords={stack}
          primaryKeyword={primary}
          onPrimaryChange={setPrimaryKw}
          onRemoveKeyword={onRemove}
          defaultInternalUrl={internalUrl}
          onGenerated={(id) => { onClear(); onGenerated(id); }}
        />
      </CardContent>
    </Card>
  );
};

export default ArticleComposer;